import React, { useState, useEffect, useCallback } from "react";
import { TrendingUp, TrendingDown, Newspaper, CloudSun, Building2, AlertTriangle, Plus, X, Activity, Landmark, Waves, Gauge, Gem } from "lucide-react";
import { nexusWatchlist, nexusAddWatch, nexusDelWatch, nexusWeather, nexusWatchtower, nexusFeed,
  nexusUwNews, nexusUwMovers, nexusUwCongress, nexusUwInsider, nexusTechnicals, nexusSpwealth } from "../lib/api";
import NexusBackdrop from "./NexusBackdrop";
import { VideoIntelMeter } from "./VideoIntelMeter";
import { toast } from "sonner";

function Panel({ title, icon: Icon, children, testid, accent = "#00F0FF", tag }) {
  return (
    <div className="nx-glass rounded-2xl p-4 flex flex-col min-h-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <span className="nx-orbitron text-xs tracking-[0.25em] font-bold" style={{ color: accent }}>{title}</span>
        {tag && <span className="ml-auto nx-mono text-[9px] text-cyan-100/40">{tag}</span>}
      </div>
      <div className="overflow-y-auto min-h-0 flex-1 space-y-2">{children}</div>
    </div>
  );
}

const SENT_COLOR = { bullish: "#10B981", bearish: "#EF4444", positive: "#10B981", negative: "#EF4444", neutral: "#64748B" };
const fmtM = (n) => `$${(Math.abs(n) / 1e6).toFixed(1)}M`;

export default function NexusIntel({ session, embedded }) {
  const [wl, setWl] = useState({ symbols: [], quotes: [] });
  const [news, setNews] = useState([]);
  const [movers, setMovers] = useState([]);
  const [congress, setCongress] = useState([]);
  const [insider, setInsider] = useState([]);
  const [weather, setWeather] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [feed, setFeed] = useState([]);
  const [picks, setPicks] = useState([]);
  const [adding, setAdding] = useState("");
  const [tech, setTech] = useState(null);
  const [techBusy, setTechBusy] = useState("");

  const loadStocks = useCallback(async () => { try { setWl(await nexusWatchlist(session.user_id)); } catch (e) {} }, [session.user_id]);

  useEffect(() => { loadStocks(); const t = setInterval(loadStocks, 30000); return () => clearInterval(t); }, [loadStocks]);
  useEffect(() => {
    (async () => {
      try { setNews((await nexusUwNews()).items || []); } catch (e) {}
      try { setMovers((await nexusUwMovers()).items || []); } catch (e) {}
      try { setCongress((await nexusUwCongress()).items || []); } catch (e) {}
      try { setInsider((await nexusUwInsider()).items || []); } catch (e) {}
      try { setWeather(await nexusWeather()); } catch (e) {}
      try { setAlerts((await nexusWatchtower(session.user_id)).alerts || []); } catch (e) {}
      try { setFeed((await nexusFeed(session.user_id)) || []); } catch (e) {}
      try { setPicks((await nexusSpwealth()).picks || []); } catch (e) {}
    })();
    const t = setInterval(async () => {
      try { setAlerts((await nexusWatchtower(session.user_id)).alerts || []); } catch (e) {}
      try { setMovers((await nexusUwMovers()).items || []); } catch (e) {}
    }, 45000);
    return () => clearInterval(t);
  }, [session.user_id]);

  const add = async () => {
    const s = adding.trim().toUpperCase(); if (!s) return;
    try { setWl(await nexusAddWatch(session.user_id, s)); setAdding(""); await loadStocks(); } catch (e) { toast.error("Could not add"); }
  };
  const del = async (s) => { try { setWl(await nexusDelWatch(session.user_id, s)); } catch (e) {} };
  const showTech = async (s) => {
    setTechBusy(s); setTech(null);
    try { const t = await nexusTechnicals(s); setTech(t); if (t.error) toast.message(`${s}: ${t.error}`); }
    catch (e) { toast.error("Could not load analysis"); } finally { setTechBusy(""); }
  };

  return (
    <div className={`relative ${embedded ? "" : "min-h-[calc(100vh-52px)]"}`} data-testid="nexus-intel">
      {!embedded && <NexusBackdrop colorHex="#00F0FF" />}
      <div className="relative z-10 p-4 grid grid-cols-1 md:grid-cols-3 auto-rows-[minmax(220px,auto)] gap-4">
      {/* Markets / watchlist */}
      <Panel title="MARKETS" icon={TrendingUp} testid="intel-stocks" accent="#10B981" tag="FINNHUB · REAL-TIME">
        <div className="flex gap-1 mb-2 sticky top-0">
          <input value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Add ticker (AAPL)" data-testid="intel-add-ticker"
            className="flex-1 bg-[#030712] text-cyan-50 border border-[#00F0FF33] rounded-lg px-2 py-1 text-xs min-w-0" />
          <button onClick={add} data-testid="intel-add-btn" className="px-2 rounded-lg bg-[#00F0FF] text-[#030712]"><Plus className="w-4 h-4" /></button>
        </div>
        {wl.quotes.length === 0 && <p className="text-cyan-100/40 text-xs">Loading quotes…</p>}
        {wl.quotes.map((q) => {
          const up = q.change >= 0;
          return (
            <div key={q.symbol} className="flex items-center justify-between text-sm group" data-testid={`intel-quote-${q.symbol}`}>
              <button onClick={() => showTech(q.symbol)} className="font-bold text-cyan-50 hover:text-[#00F0FF] flex items-center gap-1" data-testid={`intel-tech-${q.symbol}`}>
                {q.symbol} {techBusy === q.symbol && <Activity className="w-3 h-3 animate-pulse" />}
              </button>
              <div className="flex items-center gap-2">
                <span className="text-cyan-100">{q.price}</span>
                <span className={`text-xs flex items-center gap-0.5 ${up ? "text-emerald-400" : "text-red-400"}`}>
                  {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{q.change_pct}%
                </span>
                <button onClick={() => del(q.symbol)} className="opacity-0 group-hover:opacity-100 text-red-400/70"><X className="w-3 h-3" /></button>
              </div>
            </div>
          );
        })}
        {tech && !tech.error && (
          <div className="mt-2 pt-2 border-t border-[#00F0FF22] text-[11px] text-cyan-100/80" data-testid="intel-tech-readout">
            <span className="font-bold text-cyan-50">{tech.ticker}</span> · RSI {tech.rsi}
            <span className="ml-1" style={{ color: tech.signal === "overbought" ? "#EF4444" : tech.signal === "oversold" ? "#10B981" : "#64748B" }}>({tech.signal})</span>
            <span className="ml-2">MACD {tech.macd_hist > 0 ? "▲" : "▼"} </span>
            <span style={{ color: tech.trend === "bullish" ? "#10B981" : "#EF4444" }}>{tech.trend}</span>
          </div>
        )}
        <p className="text-cyan-100/30 text-[10px] mt-1">Tap a ticker for RSI / MACD analysis</p>
      </Panel>

      {/* Options flow movers (UW) */}
      <Panel title="OPTIONS FLOW" icon={Waves} testid="intel-flow" accent="#00F0FF" tag="UNUSUAL WHALES">
        {movers.length === 0 && <p className="text-cyan-100/40 text-xs">Reading the tape…</p>}
        {movers.map((m, i) => {
          const bull = (m.net_premium || 0) >= 0;
          return (
            <div key={i} className="flex items-center justify-between text-sm" data-testid="intel-mover">
              <span className="font-bold text-cyan-50">{m.ticker}</span>
              <span className={`text-xs font-semibold ${bull ? "text-emerald-400" : "text-red-400"}`}>
                {bull ? "▲ calls" : "▼ puts"} {fmtM(m.net_premium)}
              </span>
            </div>
          );
        })}
        <p className="text-cyan-100/30 text-[10px] mt-1">Net options premium — where big money is betting</p>
      </Panel>

      {/* Watchtower */}
      <Panel title="WATCHTOWER" icon={AlertTriangle} testid="intel-watchtower" accent="#F59E0B" tag={`${alerts.length}`}>
        {alerts.length === 0 && <p className="text-cyan-100/40 text-xs">All clear, sir. Nothing needs you right now.</p>}
        {alerts.map((a, i) => (
          <div key={i} className={`rounded-lg px-2 py-1.5 text-xs border-l-2 ${a.level === "danger" ? "border-red-500 bg-red-500/10" : "border-cyan-500/40 bg-cyan-500/5"}`} data-testid="intel-alert">
            <span className="font-bold text-cyan-50">{a.title}</span>
            <span className="text-cyan-100/70"> — {a.text}</span>{a.when && <span className="text-cyan-300/50"> ({a.when})</span>}
          </div>
        ))}
      </Panel>

      {/* Market news (UW, sentiment-tagged) */}
      <Panel title="MARKET NEWS" icon={Newspaper} testid="intel-news" accent="#A855F7" tag="LIVE · SENTIMENT">
        {news.length === 0 && <p className="text-cyan-100/40 text-xs">Loading headlines…</p>}
        {news.map((n, i) => {
          const col = SENT_COLOR[(n.sentiment || "neutral").toLowerCase()] || "#64748B";
          return (
            <div key={i} className="flex items-start gap-1.5 text-xs leading-snug" data-testid="intel-news-item">
              <span className="inline-block w-1.5 h-1.5 rounded-full mt-1 shrink-0" style={{ background: col }} />
              <span className="text-cyan-100 flex-1 min-w-0">
                {n.is_major && <span className="text-[9px] text-amber-400 font-bold mr-1">MAJOR</span>}
                {n.title}
                {(n.tickers || []).length > 0 && <span className="text-cyan-400/60 nx-mono"> {n.tickers.slice(0, 3).map((t) => `$${t}`).join(" ")}</span>}
              </span>
            </div>
          );
        })}
      </Panel>

      {/* Smart money — congress + insider */}
      <Panel title="SMART MONEY" icon={Landmark} testid="intel-smartmoney" accent="#FBBF24" tag="CONGRESS · INSIDERS">
        {congress.length === 0 && insider.length === 0 && <p className="text-cyan-100/40 text-xs">Watching the filings…</p>}
        {congress.slice(0, 6).map((c, i) => (
          <div key={`c${i}`} className="text-xs text-cyan-100" data-testid="intel-congress">
            <Landmark className="w-3 h-3 inline mr-1 text-amber-400/80" />
            <span className="font-bold text-cyan-50">{c.name}</span>
            <span className={c.type === "Buy" ? "text-emerald-400" : "text-red-400"}> {c.type} ${c.ticker}</span>
            <span className="text-cyan-100/50"> · {c.amount}</span>
          </div>
        ))}
        {insider.slice(0, 5).map((s, i) => (
          <div key={`i${i}`} className="text-xs text-cyan-100/80" data-testid="intel-insider">
            <Gauge className="w-3 h-3 inline mr-1 text-cyan-400/70" />
            <span className="font-semibold">${s.ticker}</span>
            <span className={s.amount >= 0 ? "text-emerald-400" : "text-red-400"}> {s.amount >= 0 ? "insider buy" : "insider sell"}</span>
            <span className="text-cyan-100/40"> · {s.owner}</span>
          </div>
        ))}
      </Panel>

      {/* Weather */}
      <Panel title="WEATHER" icon={CloudSun} testid="intel-weather" accent="#38BDF8">
        {!weather ? <p className="text-cyan-100/40 text-xs">…</p> : weather.error ? <p className="text-cyan-100/40 text-xs">{weather.city}: n/a</p> : (
          <div className="text-cyan-50">
            <p className="text-3xl font-black">{Math.round(weather.temp * 9 / 5 + 32)}°F</p>
            <p className="text-sm text-cyan-100/70">{weather.city}</p>
            <p className="text-xs text-cyan-100/50 mt-1">H {Math.round(weather.hi)}° · L {Math.round(weather.lo)}° · wind {weather.wind} km/h</p>
          </div>
        )}
      </Panel>

      {/* SP Wealth AI picks */}
      <Panel title="MY AI PICKS" icon={Gem} testid="intel-picks" accent="#C084FC" tag="SP WEALTH">
        {picks.length === 0 && <p className="text-cyan-100/40 text-xs">Your SP Wealth Tracker picks appear here.</p>}
        {picks.map((p, i) => {
          const bull = (p.direction || "").toLowerCase().includes("up") || (p.action || "").toLowerCase().includes("call");
          return (
            <div key={i} className="text-xs border-l-2 pl-2 py-0.5" style={{ borderColor: bull ? "#10B981" : "#EF4444" }} data-testid="intel-pick">
              <div className="flex items-center justify-between">
                <span className="font-bold text-cyan-50">{p.symbol}</span>
                <span className="text-[10px] text-purple-300/80">{p.tier}</span>
              </div>
              <div className="text-cyan-100/70 text-[11px]">
                {p.action} · entry ${p.entry} → tgt ${p.target}{p.rr ? ` · R/R ${p.rr}x` : ""}
              </div>
            </div>
          );
        })}
      </Panel>

      {/* Business feed */}
      <VideoIntelMeter />
      <Panel title="YOUR BUSINESS" icon={Building2} testid="intel-business" accent="#22D3EE">
        {feed.length === 0 && <p className="text-cyan-100/40 text-xs">Connect your business apps to see live numbers here.</p>}
        {feed.map((f) => (
          <div key={f.id} className="text-xs text-cyan-100"><span className="text-cyan-300/60">{f.source_name}:</span> {f.summary}</div>
        ))}
      </Panel>
      </div>
    </div>
  );
}
